import { NavLink, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import BrandLogo from '../../components/BrandLogo';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { apiGet } from '../../services/apiClient';

const menu = [
  {
    header: 'Dashboard',
    icon: 'bi-speedometer2',
    groups: [
      {
        title: 'Dashboard',
        items: [
          { label: 'Overview', to: '/admin' },
          { label: 'Analytics', to: '/admin/analytics' },
          { label: 'Visitor Tracking', to: '/admin/visitor-analytics', permission: 'REPORT_VIEW' },
        ],
      },
    ],
  },
  {
    header: 'Properties',
    icon: 'bi-house-door',
    groups: [
      {
        title: 'Properties',
        items: [
          { label: 'All Properties', to: '/admin/properties' },
          { label: 'Add Property', to: '/admin/properties/new' },
          { label: 'Pending Approval', to: '/admin/properties/pending' },
          { label: 'Categories / Types', to: '/admin/properties/types' },
          { label: 'Amenities Master', to: '/admin/amenities' },
          { label: 'Locations (Country/City/Area)', to: '/admin/locations' },
          { label: 'Policies Templates', to: '/admin/policies' },
        ],
      },
    ],
  },
  {
    header: 'Bookings',
    icon: 'bi-journal-text',
    groups: [
      {
        title: 'Bookings',
        items: [
          { label: 'All Bookings', to: '/admin/bookings' },
          { label: 'Upcoming Check-ins', to: '/admin/bookings/upcoming-checkins' },
          { label: 'Check-outs Today', to: '/admin/bookings/checkouts-today' },
          { label: 'Cancellations / Refunds', to: '/admin/bookings/cancellations-refunds' },
          { label: 'Cancellation Requests', to: '/admin/cancellations' },
          { label: 'Booking Calendar', to: '/admin/bookings/calendar' },
        ],
      },
    ],
  },
  {
    header: 'Support',
    icon: 'bi-chat-dots',
    groups: [
      {
        title: 'Support',
        items: [{ label: 'Messages Inbox', to: '/admin/messages' }],
      },
    ],
  },
  {
    header: 'Customers',
    icon: 'bi-people',
    groups: [
      {
        title: 'Customers',
        items: [
          { label: 'Customers list', to: '/admin/customers' },
          { label: 'Reviews & Ratings', to: '/admin/reviews' },
          { label: 'Support tickets', to: '/admin/support' },
        ],
      },
    ],
  },
  {
    header: 'Hosts / Partners',
    icon: 'bi-person-badge',
    groups: [
      {
        title: 'Hosts / Partners',
        items: [
          { label: 'Hosts list', to: '/admin/hosts' },
          { label: 'KYC / Verification', to: '/admin/hosts/kyc' },
          { label: 'Bank account verification', to: '/admin/hosts/bank-accounts' },
          { label: 'Host performance', to: '/admin/hosts/performance' },
        ],
      },
    ],
  },
  {
    header: 'Pricing & Deals',
    icon: 'bi-tags',
    groups: [
      {
        title: 'Pricing & Deals',
        items: [
          { label: 'Seasonal pricing', to: '/admin/pricing/seasonal' },
          { label: 'Promotions', to: '/admin/pricing/coupons' },
          { label: 'Special offers', to: '/admin/pricing/offers' },
          { label: 'Taxes & Fees', to: '/admin/pricing/taxes' },
          { label: 'Partial Payment', to: '/admin/pricing/partial-payment' },
        ],
      },
    ],
  },
  {
    header: 'Tours & Packages',
    icon: 'bi-map',
    groups: [
      {
        title: 'Tours',
        items: [
          { label: 'Travel Packages', to: '/admin/packages' },
          { label: 'Destinations', to: '/admin/packages/destinations' },
          { label: 'Themes & Categories', to: '/admin/packages/themes' },
          { label: 'Landing Page Builder', to: '/admin/packages/landing' },
          { label: 'Packages Listing Settings', to: '/admin/packages/listing' },
          { label: 'Package Enquiries', to: '/admin/packages/enquiries' },
          { label: 'Offers', to: '/admin/packages/offers' },
          { label: 'Testimonials', to: '/admin/packages/testimonials' },
          { label: 'Articles / Blogs', to: '/admin/packages/articles' },
          { label: 'Customized Tour Requests', to: '/admin/tours/requests' },
          { label: 'Itineraries / Activities', to: '/admin/packages/itineraries' },
        ],
      },
    ],
  },
  {
    header: 'Cab Services',
    icon: 'bi-car-front',
    groups: [
      {
        title: 'Cab Services',
        items: [
          { label: 'Dashboard', to: '/admin/cabs' },
          { label: 'Vehicles', to: '/admin/cabs/vehicles' },
          { label: 'Vehicle Categories', to: '/admin/cabs/vehicle-categories' },
          { label: 'Seat Configurations', to: '/admin/cabs/seat-configurations' },
          { label: 'Pricing Rules', to: '/admin/cabs/pricing-rules' },
          { label: 'Cab Packages', to: '/admin/cabs/cab-packages' },
          { label: 'Transport Packages', to: '/admin/cabs/transport-packages' },
          { label: 'Package Variants', to: '/admin/cabs/package-variants' },
          { label: 'Locations', to: '/admin/cabs/locations' },
          { label: 'Routes', to: '/admin/cabs/routes' },
          { label: 'Amenities', to: '/admin/cabs/amenities' },
          { label: 'Coupons', to: '/admin/cabs/coupons' },
          { label: 'Popular Routes', to: '/admin/cabs/popular-routes' },
          { label: 'Hero Banners', to: '/admin/cabs/hero-banners' },
          { label: 'Vehicle Banner', to: '/admin/cabs/vehicle-banner' },
          { label: 'Guest Trips', to: '/admin/cabs/guest-trips' },
          { label: 'Blogs', to: '/admin/cabs/blogs' },
          { label: 'Promo Videos', to: '/admin/cabs/promo-videos' },
          { label: 'Footer', to: '/admin/cabs/footer' },
          { label: 'Enquiries', to: '/admin/cabs/enquiries' },
          { label: 'Reviews', to: '/admin/cabs/reviews' },
          { label: 'Landing Page Builder', to: '/admin/cabs/cms' },
          { label: 'Settings', to: '/admin/cabs/settings' },
        ],
      },
    ],
  },
  {
    header: 'Finance & Accounts',
    icon: 'bi-wallet2',
    groups: [
      {
        title: 'Finance & Accounts',
        items: [
          { label: 'Ledger Summary', to: '/admin/finance/summary' },
          { label: 'Ledger Entries', to: '/admin/finance/entries' },
          { label: 'Ledger Accounts', to: '/admin/finance/accounts' },
          { label: 'Invoices', to: '/admin/invoices' },
          { label: 'Transactions', to: '/admin/payments/transactions' },
          { label: 'Settlements', to: '/admin/payments/settlements' },
          { label: 'Refunds', to: '/admin/payments/refunds' },
          { label: 'Payment settings', to: '/admin/payments/settings' },
          { label: 'Payouts', to: '/admin/payouts' },
        ],
      },
    ],
  },
  {
    header: 'Content',
    icon: 'bi-images',
    groups: [
      {
        title: 'Content',
        items: [
          { label: 'Banners / Home page sections', to: '/admin/content/banners' },
          { label: 'Landing Hero Banner', to: '/admin/content/hero-banner' },
          { label: 'Popular Destinations', to: '/admin/content/popular-destinations' },
          { label: 'Hero Panels Slider (3-panel)', to: '/admin/content/hero-panels' },
          { label: 'Blogs / FAQs', to: '/admin/content/blogs' },
          { label: 'Static pages (Terms, Privacy, About)', to: '/admin/content/pages' },
          { label: 'Footer Management', to: '/admin/content/footer' },
        ],
      },
    ],
  },
  {
    header: 'Email Center',
    icon: 'bi-envelope-paper',
    groups: [
      {
        title: 'Email Center',
        items: [
          { label: 'Dashboard', to: '/admin/email' },
          { label: 'Mail Logs', to: '/admin/email/logs' },
          { label: 'Campaigns', to: '/admin/email/campaigns' },
          { label: 'Templates', to: '/admin/email/templates' },
          { label: 'Audience / Recipients', to: '/admin/email/audience' },
          { label: 'Queue / Retry', to: '/admin/email/queue' },
          { label: 'Settings / SMTP Status', to: '/admin/email/settings' },
          { label: 'Analytics / Reports', to: '/admin/email/reports' },
        ],
      },
    ],
  },
  {
    header: 'Reports',
    icon: 'bi-bar-chart',
    groups: [
      {
        title: 'Reports',
        items: [
          { label: 'Revenue reports', to: '/admin/reports/revenue' },
          { label: 'Occupancy reports', to: '/admin/reports/occupancy' },
          { label: 'Property performance', to: '/admin/reports/property' },
          { label: 'Tour performance', to: '/admin/reports/tour' },
          { label: 'Host reports', to: '/admin/reports/hosts' },
          { label: 'Client reports', to: '/admin/reports/clients' },
        ],
      },
    ],
  },
  {
    header: 'API Management',
    icon: 'bi-diagram-3',
    groups: [
      {
        title: 'Channel Manager',
        items: [
          { label: 'Providers', to: '/admin/channel/providers' },
          { label: 'Connections', to: '/admin/channel/connections' },
          { label: 'Property Mappings', to: '/admin/channel/mappings' },
          { label: 'Sync Monitor', to: '/admin/channel/monitor' },
        ],
      },
    ],
  },
  {
    header: 'Settings',
    icon: 'bi-gear',
    groups: [
      {
        title: 'Settings',
        items: [
          { label: 'Roles & permissions', to: '/admin/settings/roles', permission: 'ROLE_MANAGE' },
          { label: 'Sub Admins', to: '/admin/settings/sub-admins', permission: 'ROLE_MANAGE' },
          { label: 'Security', to: '/admin/settings/security' },
          { label: 'Audit logs', to: '/admin/settings/audit-logs', permission: 'AUDIT_VIEW' },
          { label: 'Search Configuration', to: '/admin/settings/search-configuration', permission: 'SETTINGS_MANAGE' },
          { label: 'Email/SMS/WhatsApp templates', to: '/admin/settings/templates', permission: 'SETTINGS_MANAGE' },
          { label: 'System settings', to: '/admin/settings/system', permission: 'SETTINGS_MANAGE' },
          { label: 'API integrations', to: '/admin/settings/integrations', permission: 'API_MANAGE' },
        ],
      },
    ],
  },
];

const sectionPermissions = {
  Dashboard: ['DASHBOARD_VIEW', 'REPORT_VIEW'],
  Properties: ['PROPERTY_VIEW', 'PROPERTY_MANAGE'],
  Bookings: ['BOOKING_VIEW', 'BOOKING_MANAGE'],
  Support: ['SUPPORT_VIEW', 'SUPPORT_MANAGE'],
  Customers: ['BOOKING_VIEW', 'SUPPORT_VIEW', 'SUPPORT_MANAGE'],
  'Hosts / Partners': ['HOST_VIEW', 'HOST_MANAGE', 'KYC_VIEW', 'KYC_MANAGE'],
  'Pricing & Deals': ['COUPON_MANAGE', 'PAYMENT_MANAGE', 'PAYMENT_VIEW'],
  'Tours & Packages': ['PROPERTY_VIEW', 'PROPERTY_MANAGE', 'BANNER_MANAGE', 'BLOG_MANAGE'],
  'Cab Services': ['PROPERTY_VIEW', 'PROPERTY_MANAGE', 'SUPPORT_VIEW', 'SUPPORT_MANAGE'],
  'Finance & Accounts': ['PAYMENT_VIEW', 'PAYMENT_MANAGE', 'PAYOUT_MANAGE', 'REPORT_VIEW'],
  Content: ['BANNER_MANAGE', 'BLOG_MANAGE'],
  'Email Center': ['EMAIL_VIEW', 'EMAIL_MANAGE', 'REPORT_VIEW', 'BLOG_MANAGE'],
  Reports: 'REPORT_VIEW',
  'API Management': 'API_MANAGE',
  Settings: ['ROLE_MANAGE', 'SETTINGS_MANAGE', 'API_MANAGE', 'AUDIT_VIEW'],
};

const isItemActive = (path, itemPath) => {
  if (!itemPath) return false;
  if (itemPath === '/admin') return path === '/admin' || path === '/admin/dashboard';
  return path === itemPath || path.startsWith(`${itemPath}/`);
};

const AdminSidebar = ({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) => {
  const location = useLocation();
  const { hasPermission, isSuperAdmin, permissions } = useAdminAuth();
  const [packageEnquiryUnreadCount, setPackageEnquiryUnreadCount] = useState(0);
  const [openHeaders, setOpenHeaders] = useState(() =>
    Object.fromEntries(menu.map((section) => [section.header, false]))
  );

  const canSee = useCallback((permission) => {
    if (!permission) return true;
    if (isSuperAdmin) return true;
    if (!permissions || permissions.length === 0) return false;
    if (Array.isArray(permission)) {
      return permission.some((perm) => hasPermission(perm));
    }
    return hasPermission(permission);
  }, [hasPermission, isSuperAdmin, permissions]);

  const visibleMenu = useMemo(() => {
    return menu
      .filter((section) => canSee(sectionPermissions[section.header]))
      .map((section) => ({
        ...section,
        groups: (section.groups || [])
          .map((group) => ({
            ...group,
            items: (group.items || []).filter((item) => canSee(item.permission)),
          }))
          .filter((group) => group.items.length > 0),
      }))
      .filter((section) => section.groups.length > 0);
  }, [canSee]);

  const shellWidth = collapsed
    ? 'w-[var(--admin-sidebar-collapsed-width)]'
    : 'w-[var(--admin-sidebar-width)]';
  const translate = mobileOpen ? 'translate-x-0' : '-translate-x-full';
  const activePath = location.pathname;

  useEffect(() => {
    const activeSection = visibleMenu.find((section) =>
      section.groups?.some((group) => group.items?.some((item) => isItemActive(location.pathname, item.to)))
    );
    if (!activeSection) return;

    setOpenHeaders((prev) => {
      if (prev[activeSection.header]) return prev;
      const next = Object.fromEntries(menu.map((section) => [section.header, false]));
      next[activeSection.header] = true;
      return next;
    });
  }, [location.pathname, visibleMenu]);

  useEffect(() => {
    let active = true;
    const loadUnreadCount = async () => {
      try {
        const res = await apiGet('/api/admin/package-enquiries?read=false&limit=1');
        if (!active) return;
        setPackageEnquiryUnreadCount(Number(res?.meta?.unreadCount || 0));
      } catch (_) {
        if (!active) return;
        setPackageEnquiryUnreadCount(0);
      }
    };

    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 30000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const toggleSection = (header) => {
    setOpenHeaders((prev) => {
      const next = Object.fromEntries(menu.map((section) => [section.header, false]));
      next[header] = !prev[header];
      return next;
    });
  };

  const handleNavigate = () => {
    onCloseMobile?.();
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-950/60 transition-opacity lg:hidden ${
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onCloseMobile}
        role="button"
        tabIndex={-1}
        aria-label="Close sidebar"
      />
      <aside
        className={`fixed left-0 top-0 z-50 h-full ${shellWidth} ${translate} transition-all duration-300 lg:translate-x-0`}
      >
        <div className="admin-sidebar flex h-full flex-col shadow-xl">
          <div className={`flex ${collapsed ? 'flex-col items-center gap-3 px-3 py-4' : 'items-center justify-between px-4 py-4'}`}>
            <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
              <BrandLogo
                size={collapsed ? 36 : 40}
                className="rounded-lg bg-white"
                imgClassName="p-1"
                fallback="HV"
                fallbackClassName="text-slate-900 font-semibold"
              />
              {!collapsed && (
                <div>
                  <p className="admin-sidebar-label text-[rgba(152,162,179,0.92)]">Homevilas</p>
                  <p className="text-sm font-semibold text-white">Admin Console</p>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onToggleCollapse}
              className="hidden h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-sm text-white transition hover:bg-white/10 lg:flex"
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} />
            </button>
          </div>

          {collapsed ? (
            <nav className="mt-3 flex-1 space-y-2 px-3">
              {visibleMenu.map((section) => {
                const firstItem = section.groups?.[0]?.items?.[0];
                if (!firstItem) return null;
                const sectionActive = section.groups?.some((group) =>
                  group.items?.some((item) => isItemActive(activePath, item.to))
                );
                return (
                  <NavLink
                    key={section.header}
                    to={firstItem.to}
                    title={section.header}
                    onClick={handleNavigate}
                    className={`admin-sidebar-item group relative flex items-center justify-center px-3 py-2 text-sm font-medium ${
                      sectionActive ? 'active text-white' : 'text-[rgba(226,232,240,0.82)]'
                    }`}
                  >
                    <i className={`bi ${section.icon} text-base`} />
                    <span className="admin-tooltip pointer-events-none absolute left-full ml-3 opacity-0 transition group-hover:opacity-100">
                      {section.header}
                    </span>
                  </NavLink>
                );
              })}
            </nav>
          ) : (
            <nav className="mt-3 flex-1 space-y-4 overflow-y-auto px-3 pb-6">
              {visibleMenu.map((section) => {
                const isOpen = openHeaders[section.header];
                const headerActive = section.groups?.some((group) =>
                  group.items?.some((item) => isItemActive(location.pathname, item.to))
                );
                return (
                  <section
                    key={section.header}
                    className={`rounded-[24px] border px-3 py-3 transition ${
                      headerActive
                        ? 'border-[rgba(15,92,90,0.72)] bg-[rgba(15,92,90,0.14)] shadow-[0_16px_30px_rgba(3,10,18,0.22)]'
                        : 'border-white/8 bg-white/[0.02]'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSection(section.header)}
                      aria-expanded={isOpen}
                      className="flex w-full items-start justify-between gap-3 px-1 pb-3 text-left"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/12 bg-black/20 text-white">
                          <i className={`bi ${section.icon}`} />
                        </span>
                        <div className="min-w-0">
                          <p className="admin-sidebar-label text-[rgba(152,162,179,0.78)]">{section.header}</p>
                          <p className="text-sm font-semibold text-slate-50">{section.groups[0]?.title || section.header}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 pt-0.5">
                        <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-sm text-[rgba(152,162,179,0.95)]`} />
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden border-t border-white/8 transition-all duration-300 ${
                        isOpen ? 'max-h-[640px] pt-3 opacity-100' : 'max-h-0 pt-0 opacity-0'
                      }`}
                    >
                      <div className="space-y-3">
                      {section.groups.map((group) => (
                        <div key={group.title} className="space-y-1">
                          {section.groups.length > 1 && (
                            <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-[rgba(226,232,240,0.42)]">
                              {group.title}
                            </p>
                          )}
                          {group.items.map((item) => (
                            <NavLink
                              key={item.label}
                              to={item.to}
                              onClick={handleNavigate}
                              className={({ isActive }) =>
                                [
                                  'admin-sidebar-item flex items-center gap-3 rounded-[18px] border px-3 py-3 text-sm transition',
                                  isActive
                                    ? 'active border-white/10 text-white'
                                    : 'border-white/8 text-[rgba(226,232,240,0.84)] hover:border-white/16 hover:text-white',
                                ].join(' ')
                              }
                            >
                              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[rgba(201,161,91,0.88)]" />
                              <span className="min-w-0 flex-1 whitespace-normal leading-5">
                                {item.label}
                              </span>
                              {item.to === '/admin/packages/enquiries' && packageEnquiryUnreadCount > 0 ? (
                                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                  {packageEnquiryUnreadCount}
                                </span>
                              ) : null}
                            </NavLink>
                          ))}
                        </div>
                      ))}
                      </div>
                    </div>
                  </section>
                );
              })}
            </nav>
          )}
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
